import {compact, isEmpty} from 'lodash'
import {ApolloClient, InMemoryCache, useMutation as useApolloMutation, HttpLink, ApolloLink} from '@apollo/client'
import {setFlash} from './flash'
import {createConsumer} from '@rails/actioncable'
import {ActionCableLink} from './actionCableLink'

export * from '@apollo/client'

const cache = new InMemoryCache({
  possibleTypes: {
    StatementItem: ['Invoice', 'RepeatingInvoice', 'Payment', 'Credit', 'Debit'],
    Alert: ['ServiceAlert', 'AccountAlert']
  },
  typePolicies: {
    Account: {
      fields: {
        displayName: {
          read(_, {readField}) {
            return readField('name') && readField('name').length
              ? readField('name')
              : compact([readField('contactFirstName'), readField('contactLastName')]).join(' ')
          }
        },
        contactFullName: {
          read(_, {readField}) {
            return compact([readField('contactFirstName'), readField('contactLastName')]).join(' ')
          }
        }
      }
    },
    User: {
      fields: {
        displayName: {
          read(_, {readField}) {
            const fullName = compact([readField('firstName'), readField('lastName')]).join(' ')
            if (fullName.length) return fullName
            return readField('email')
          }
        },
        fullName: {
          read(_, {readField}) {
            return compact([readField('firstName'), readField('lastName')]).join(' ')
          }
        }
      }
    },
    Invoice: {
      fields: {
        description: {
          read(_, {readField}) {
            let lineItems = readField('lineItems')
            return lineItems && compact(lineItems.map(lineItem => lineItem.description)).join('\n')
          }
        }
      }
    },
    RepeatingInvoice: {
      fields: {
        description: {
          read(_, {readField}) {
            let lineItems = readField('lineItems')
            return lineItems && compact(lineItems.map(lineItem => lineItem.description)).join('\n')
          }
        }
      }
    }
  }
})

const hasSubscriptionOperation = ({query: {definitions}}) => {
  return definitions.some(({kind, operation}) => {
    return kind === 'OperationDefinition' && operation === 'subscription'
  })
}

export default ({organisationId}) => {
  const cable = createConsumer(`/cable?organisation_id=${organisationId}`)
  const actionCableLink = new ActionCableLink(cable)
  const httpLink = new HttpLink({uri: `/orgs/${organisationId}/graphql`})
  const link = ApolloLink.split(hasSubscriptionOperation, actionCableLink, httpLink)
  return new ApolloClient({link, cache, connectToDevTools: true})
}

const NESTED_ATTRS_REGEX = /^(.+)\[([0-9]+)\]\.(.+)$/

export const useMutation = (gql, globalOptions = {}) => {
  const flash = globalOptions.flash
  delete globalOptions.flash

  const globalOnCompleted = data => {
    if (flash) setFlash(flash)
    if (globalOptions.onCompleted) setTimeout(() => globalOptions.onCompleted(data), 0)
  }

  const globalOnError = error => {
    if (isEmpty(error.graphQLErrors)) return
    const errorOnBase = error.graphQLErrors.find(({extensions}) => extensions.attribute === 'base')
    if (errorOnBase) {
      setFlash(errorOnBase.extensions.messages.join('; '), {type: 'alert'})
    } else {
      setFlash('There are error(s) on the form', {type: 'alert'})
    }
    if (globalOptions.onError) setTimeout(() => globalOptions.onError(error), 0)
  }

  const [apolloMutate, apolloResult] = useApolloMutation(gql, {
    ...globalOptions,
    onCompleted: globalOnCompleted,
    onError: globalOnError
  })

  let error = null
  if (apolloResult.error) {
    apolloResult.error.graphQLErrors.forEach(({extensions}) => {
      if (extensions && extensions.attribute && extensions.messages) {
        error ||= {}
        if (NESTED_ATTRS_REGEX.test(extensions.attribute)) {
          let [_match, parent, index, attribute] = NESTED_ATTRS_REGEX.exec(extensions.attribute)
          error[parent] ||= []
          error[parent][index] ||= {}
          error[parent][index][attribute] = extensions.messages
        } else {
          error[extensions.attribute] = extensions.messages
        }
      }
    })
    if (isEmpty(error)) console.error(apolloResult.error)
  }

  const mutate = (variables, onCompleted) => {
    let options = {variables}
    if (onCompleted) {
      options.onCompleted = data => {
        globalOnCompleted(data)
        onCompleted(data)
      }
    }
    return apolloMutate(options)
  }

  return [mutate, {...apolloResult, error}]
}
